import React, { useState } from "react";
import { motion } from "framer-motion";
import { Send, Loader2, TrendingUp, Bot, Workflow } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { useToast } from "../hooks/use-toast";
import { contactCta } from "../mock";

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Missing details", description: "Please fill name, email and message before sending." });
      return;
    }
    setLoading(true);
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Server error");
      toast({ title: "Message sent!", description: "Our team will get back to you within 24 hours." });
      setForm({ name: "", email: "", company: "", message: "" });
    } catch {
      toast({ title: "Send failed", description: "Please try again or email us directly.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-14 lg:py-20">
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5"
          >
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-[#73100D]/25 bg-[#73100D]/5 text-[#73100D] text-[11px] font-semibold tracking-[0.18em] uppercase mb-5">
              {contactCta.pill}
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-[#111] leading-tight">
              {contactCta.title}{" "}
              <span className="brand-gradient-text">{contactCta.highlight}</span>
            </h2>
            <p className="mt-5 text-[#555] leading-relaxed max-w-md">{contactCta.description}</p>

            <div className="mt-8 space-y-3">
              {[
                { icon: Bot, label: "AI agents that work 24/7" },
                { icon: Workflow, label: "Cut manual work by 60%+" },
                { icon: TrendingUp, label: "Tangible ROI in 90 days" },
              ].map((b, i) => (
                <motion.div
                  key={b.label}
                  initial={{ opacity: 0, x: -16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.15 + i * 0.08 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-9 h-9 rounded-lg bg-[#73100D]/10 grid place-items-center">
                    <b.icon className="w-4 h-4 text-[#73100D]" />
                  </div>
                  <span className="text-sm font-medium text-[#333]">{b.label}</span>
                </motion.div>
              ))}
            </div>

          </motion.div>

          <motion.form
            onSubmit={onSubmit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7 rounded-3xl border border-[#E5E5E5] bg-white p-8 md:p-10 space-y-5 soft-shadow"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Your Name">
                <Input name="name" value={form.name} onChange={onChange} placeholder="Jane Doe"
                  className="bg-white border-[#E5E5E5] text-[#333] placeholder:text-[#aaa] h-12 focus-visible:ring-[#73100D]/30 focus-visible:border-[#73100D]" />
              </Field>
              <Field label="Email Address">
                <Input name="email" type="email" value={form.email} onChange={onChange} placeholder="jane@company.com"
                  className="bg-white border-[#E5E5E5] text-[#333] placeholder:text-[#aaa] h-12 focus-visible:ring-[#73100D]/30 focus-visible:border-[#73100D]" />
              </Field>
            </div>
            <Field label="Company">
              <Input name="company" value={form.company} onChange={onChange} placeholder="Acme Corp"
                className="bg-white border-[#E5E5E5] text-[#333] placeholder:text-[#aaa] h-12 focus-visible:ring-[#73100D]/30 focus-visible:border-[#73100D]" />
            </Field>
            <Field label="What process do you want to automate?">
              <Textarea name="message" value={form.message} onChange={onChange} rows={5}
                placeholder="Tell us about your manual workflow, current pain points and growth goals..."
                className="bg-white border-[#E5E5E5] text-[#333] placeholder:text-[#aaa] focus-visible:ring-[#73100D]/30 focus-visible:border-[#73100D]" />
            </Field>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <p className="text-xs text-[#888]">We respect your privacy. No spam, ever.</p>
              <button disabled={loading} type="submit"
                className="btn-primary inline-flex items-center justify-center rounded-full font-semibold px-7 h-12 text-sm disabled:opacity-70">
                <span className="inline-flex items-center gap-2">
                  {loading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending...</>) : (<>Book a Strategy Call <Send className="w-4 h-4" /></>)}
                </span>
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-[0.18em] text-[#666] mb-2 font-semibold">{label}</div>
      {children}
    </label>
  );
}
